import * as Sentry from '@sentry/browser'
import useAxios from 'axios-hooks'
import type { UseAxiosResult } from 'axios-hooks'
import i18n from 'config/i18n'
import { fetchAPI, IParam } from 'libs/apis'
import { AxiosRequestConfig } from 'axios'
import * as amplitude from '@amplitude/analytics-browser'

import { LIMIT_PAGE } from 'helpers/constants'

import type {
  IParamProduct,
  IResponseService,
  Product,
  ProductForApiSubmit,
  ProductForList,
  ProductForUpdateApiSubmit,
} from 'types'
import { IResponseProduct, IResponseProductHistory } from './model'
import { createEventProperties } from 'helpers/amplitudeTracker'

export const useFindAll = (
  branchId: string,
  params?: IParamProduct,
): UseAxiosResult<ProductForList[]> => {
  const _params = { _limit: LIMIT_PAGE, _page: 1, branchId, ...params }
  const path = `${window._env_.REACT_APP_API_URL}/v1/product`

  return useAxios({
    url: path,
    params: _params,
  })
}

export const findAll = async (
  params?: IParamProduct,
  options?: AxiosRequestConfig,
): Promise<{ body: Product[]; totals: number }> => {
  const _params = { _limit: LIMIT_PAGE, _page: 1, ...params }

  const response = await fetchAPI<Product[]>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'GET',
    path: '/v1/product',
    params: _params,
    options,
  })

  if (response.status !== 200) {
    throw new Error(i18n.t('error:notFound.title'))
  }

  return {
    body: response.body as Product[],
    totals: Number(response.headers['x-total-count']),
  }
}

export const useFindOneById = (id: string): UseAxiosResult<Product> => {
  const path = `${window._env_.REACT_APP_API_URL}/v1/product/${id}`

  return useAxios(path)
}

export const findProductByIds = async (
  ids: string[],
): Promise<IResponseService<IResponseProduct[]>> => {
  try {
    const response = await fetchAPI<IResponseProduct[]>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: '/v1/product/ids',
      params: { ids },
    })

    return { data: response.body, count: +response.headers['x-total-count'] }
  } catch (error) {
    Sentry.captureException(error)

    return { error }
  }
}

export const findProductHistoriesByProductId = async (
  productId: string,
  params: IParam,
): Promise<IResponseService<IResponseProductHistory[]>> => {
  try {
    const response = await fetchAPI<IResponseProductHistory[]>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: `/v1/product/${productId}/histories`,
      params: {
        ...params,
        ...(params._sort === 'issueType' && {
          _sort: 'task.type',
        }),
      },
    })

    return { data: response.body, count: +response.headers['x-total-count'] }
  } catch (error) {
    Sentry.captureException(error)

    return { error }
  }
}

export const create = async (
  payload: ProductForApiSubmit,
  handleOpenSuccess: () => void,
): Promise<void> => {
  try {
    const response = await fetchAPI({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'POST',
      path: '/v1/product',
      options: { data: payload },
    })

    const isSuccess = response.status === 200 || response.status === 201
    if (isSuccess) {
      const eventProperties = { ...createEventProperties(), product: payload }
      amplitude.track('FixTab Backoffice: Create Product', eventProperties)

      handleOpenSuccess()
    } else {
      throw new Error(i18n.t('error:notFound.title'))
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

export const update = async (
  id: string,
  payload: ProductForUpdateApiSubmit,
  handleOpenSuccess: () => void,
): Promise<void> => {
  try {
    const response = await fetchAPI({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'PATCH',
      path: `/v1/product/${id}`,
      options: { data: payload },
    })

    const isSuccess = response.status === 200 || response.status === 201
    if (isSuccess) {
      const eventProperties = { ...createEventProperties(), product: payload }
      const action = payload.isActive ? 'Update' : 'Delete'
      amplitude.track(`FixTab Backoffice: ${action} Product`, eventProperties)

      handleOpenSuccess()
    } else {
      throw new Error(i18n.t('error:notFound.title'))
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

export const downloadProductTemplate = async (): Promise<Blob> => {
  const response = await fetchAPI<{
    arrayBuffer(): Blob
  }>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'GET',
    path: '/v1.2/product/import/template',
    responseType: 'blob',
  })

  return response.body.arrayBuffer()
}
